import { moment } from "../date/moment";
import { Holidays } from "../holidays";
import type { Enums } from "../types";

/**
 * When an invoice counts as received. An email that arrives outside its workspace's business
 * hours — at night, on a weekend, on a public holiday — is received when the office next opens,
 * because no deadline runs while nobody is there to read it. The email's own arrival stays on
 * `incoming_emails.received_at`; this is the moment `incoming_invoices.received_at` records.
 */

/** One weekday a workspace is open: Monday-first `weekday`, `HH:mm` in German time. */
export interface BusinessHoursDay {
	weekday: number;
	startsAt: string;
	endsAt: string;
}

/** Where nobody has set anything: Monday to Friday, 07:00 to 17:00. */
export const DEFAULT_BUSINESS_HOURS: BusinessHoursDay[] = [0, 1, 2, 3, 4].map((weekday) => ({
	weekday,
	startsAt: "07:00",
	endsAt: "17:00",
}));

/** How far ahead the next opening is looked for — past any run of closed days a calendar has. */
const SEARCH_DAYS = 31;

const holidaysByKey = new Map<string, Set<string>>();

/**
 * Whether a day is a public holiday nationwide or in the workspace's Bundesland.
 *
 * @param date - The day, `YYYY-MM-DD`.
 * @param region - The workspace's Bundesland, or null for the nationwide holidays alone.
 */
export function isBusinessHoliday(date: string, region: Enums<"region"> | null) {
	const year = Number(date.slice(0, 4));
	const key = `${year}:${region ?? "BUND"}`;
	let holidays = holidaysByKey.get(key);
	if (!holidays) {
		holidays = new Set(
			Holidays.getHolidays(year, region ?? "BUND").map((holiday) => holiday.dateString),
		);
		holidaysByKey.set(key, holidays);
	}
	return holidays.has(date);
}

/**
 * The moment an email counts as received: the arrival itself while the workspace is open, else the
 * next opening — skipping closed weekdays and public holidays. A calendar with no open day at all
 * keeps the arrival, since there is no opening to move it to.
 *
 * @param arrival - When the email arrived.
 * @param hours - The workspace's open weekdays; empty or null falls back to the default.
 * @param region - The workspace's Bundesland for its holidays, or null for the nationwide ones.
 * @returns The receipt, as a Date.
 */
export function getBusinessReceipt(
	arrival: Date | string,
	hours: BusinessHoursDay[] | null,
	region: Enums<"region"> | null,
): Date {
	const calendar = hours?.length ? hours : DEFAULT_BUSINESS_HOURS;
	const local = moment.tz(arrival, "Europe/Berlin");
	for (let offset = 0; offset < SEARCH_DAYS; offset++) {
		const day = local.clone().startOf("day").add(offset, "days");
		const date = day.format("YYYY-MM-DD");
		const open = findDay(calendar, day.weekday());
		if (!open || isBusinessHoliday(date, region)) continue;
		const opensAt = moment.tz(`${date} ${open.startsAt}`, "YYYY-MM-DD HH:mm", "Europe/Berlin");
		const closesAt = moment.tz(`${date} ${open.endsAt}`, "YYYY-MM-DD HH:mm", "Europe/Berlin");
		if (offset > 0 || local.isBefore(opensAt)) return opensAt.toDate();
		if (local.isBefore(closesAt)) return local.toDate();
	}
	return local.toDate();
}

function findDay(calendar: BusinessHoursDay[], weekday: number) {
	for (const day of calendar) if (day.weekday === weekday) return day;
	return null;
}
